import { createContext, ReactNode, useContext, useMemo } from 'react';

interface BeneficiaryDesignationsFormContextValue {
  /**
   * The retirement account's owner, used to default a spouse beneficiary to the
   * marital deduction recipient type. Sourced from the account entity itself so
   * defaulting works even when the diagram sync state isn't available (e.g.
   * editing from the entity overview rather than the waterfall split-screen).
   * Null when there's no owner (or for insurance, which is out of scope).
   */
  accountDecedentClientId: string | null;
}

const BeneficiaryDesignationsFormContext = createContext<
  BeneficiaryDesignationsFormContextValue | undefined
>(undefined);

export function BeneficiaryDesignationsFormProvider({
  accountDecedentClientId,
  children,
}: {
  accountDecedentClientId: string | null;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({ accountDecedentClientId }),
    [accountDecedentClientId]
  );

  return (
    <BeneficiaryDesignationsFormContext.Provider value={value}>
      {children}
    </BeneficiaryDesignationsFormContext.Provider>
  );
}

/**
 * Returns `undefined` outside the provider so callers fall back to their
 * prior decedent resolution. All current consumers (the per-recipient
 * default hook and the split modal's selector, which mounts inside the BDG
 * form tree) render under the provider and share the account-owner decedent.
 */
export function useOptionalBeneficiaryDesignationsFormContext() {
  return useContext(BeneficiaryDesignationsFormContext);
}
